import type { HubId, Navigation, NavigationPageItem } from 'types/navigation'

export const SIDEBAR_PIN_LIMIT = 24

export interface PinnablePage {
  id: string
  title: string
  segment: string
  path?: string
  href?: string
  icon?: NavigationPageItem['icon']
  hub?: HubId
}

const isPage = (item: Navigation[number]): item is NavigationPageItem =>
  item.kind !== 'header' && item.kind !== 'divider'

export function normalizeSidebarPins(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  const seen = new Set<string>()
  const pins: string[] = []
  for (const entry of value) {
    if (typeof entry !== 'string') continue
    const id = entry.trim()
    if (!id || seen.has(id)) continue
    seen.add(id)
    pins.push(id)
    if (pins.length >= SIDEBAR_PIN_LIMIT) break
  }
  return pins
}

export function toggleSidebarPin(pins: readonly string[], id: string): string[] {
  const clean = id.trim()
  if (!clean) return normalizeSidebarPins(pins)
  if (pins.includes(clean)) return pins.filter(pin => pin !== clean)
  return normalizeSidebarPins([...pins, clean])
}

/** Leaf pages a user can pin. Ids are full segments, or href when there is no route. */
export function collectPinnablePages(items: Navigation, parentSegment?: string): PinnablePage[] {
  const pages: PinnablePage[] = []

  for (const item of items) {
    if (!isPage(item)) continue

    if (item.children && item.children.length > 0) {
      pages.push(...collectPinnablePages(item.children, item.segment))
      continue
    }

    if (!item.title) continue

    const segment =
      parentSegment && item.segment ? `${parentSegment}/${item.segment}` : item.segment || ''
    const id = segment || item.href || ''
    if (!id) continue

    pages.push({
      id,
      title: item.title,
      segment,
      path: item.href ? undefined : segment ? `/${segment}` : undefined,
      href: item.href,
      icon: item.icon,
      hub: item.hub,
    })
  }

  return pages
}

/** True when this route is one of the user's pins (exact page or a child path). */
export function isPinnedRoute(
  pages: readonly PinnablePage[],
  pins: readonly string[],
  pathname: string,
): boolean {
  const normalized = pathname.replace(/\/+$/, '') || '/'
  return pagesForPins(pages, pins).some(page => {
    if (!page.path) return false
    const path = page.path.replace(/\/+$/, '') || '/'
    return normalized === path || normalized.startsWith(`${path}/`)
  })
}

export function pagesForPins(pages: readonly PinnablePage[], pins: readonly string[]): PinnablePage[] {
  const byId = new Map(pages.map(page => [page.id, page]))
  return pins.flatMap(id => {
    const page = byId.get(id)
    return page ? [page] : []
  })
}
